/**
 * `MapThemeProvider` + `useMapTheme()` — resolves which `MapTheme` (legacy or
 * editorial) a Map surface renders against, from the per-team
 * `task_editorial_ui` flag.
 *
 * The editorial revamp is gated + migrated journey-by-journey (founder
 * cadence, 2026-05-26): the flag stays OFF for every team until every journey
 * is migrated, then it's flipped ON for all teams (one registry-default flip,
 * like PR #1042). So:
 *   - flag `true`  → editorial theme,
 *   - flag `false`/`undefined` (off, loading, no team, errored) → legacy.
 * The default context value is the legacy theme, so any Map atom rendered
 * outside a provider (e.g. a standalone unit test) safely renders legacy with
 * no extra setup.
 *
 * A component is "migrated" by reading `useMapTheme().palette` /
 * `.stateStyle` / `.fonts` instead of importing `tree/palette`'s static
 * `PALETTE` / `tree/state`'s `taskStateStyle` — a mechanical swap, since the
 * palette keys match. See `theme/tokens.ts`.
 *
 * Mounting: wrap each routed Map page (`MapHomePage` etc.). The provider
 * reads `:teamId` from the route and calls `useTeamFeature` — the same
 * read `MapEditor` already makes for the other `task_*` flags, so it adds no
 * new provider requirement (AuthProvider + QueryClient are already required
 * by the Map surfaces).
 */

import { type ReactNode, createContext, useContext } from "react";
import { useParams } from "react-router-dom";

import { useTeamFeature } from "../../api/useTeamFeature";
import { EDITORIAL_THEME, LEGACY_THEME, type MapTheme } from "./tokens";

import "./editorial.css";

/** The per-team registry key that gates the editorial Map look (default OFF). */
export const EDITORIAL_UI_KEY = "task_editorial_ui";

const MapThemeContext = createContext<MapTheme>(LEGACY_THEME);

/** Read the resolved Map theme. Legacy when no provider is present. */
export function useMapTheme(): MapTheme {
  return useContext(MapThemeContext);
}

export function MapThemeProvider({
  children,
  teamId: teamIdProp,
}: {
  children: ReactNode;
  /** Override the route param (tests / non-routed mounts). */
  teamId?: string;
}) {
  const params = useParams<{ teamId: string }>();
  const teamId = teamIdProp ?? params.teamId;
  const editorial = useTeamFeature(teamId, EDITORIAL_UI_KEY) === true;
  const theme = editorial ? EDITORIAL_THEME : LEGACY_THEME;
  return <MapThemeContext.Provider value={theme}>{children}</MapThemeContext.Provider>;
}
